// ── naverPrices.js — 네이버 쇼핑 최저가 fetch + 단위 정규화 ─────
// 가격 비교는 모두 원/kg 기준으로 통일.
// 검색 전 동의어 사전(synonyms.js) 적용: "백오이" → "오이"로 검색.

import { normalizeFoodName } from './synonyms';

const CACHE = new Map();
const CLIENT_TTL = 6 * 60 * 60 * 1000;

export async function fetchNaverShopping(query) {
  const q = String(query || '').trim();
  if (!q) return { ok: false, error: 'empty query', items: [] };

  const cached = CACHE.get(q);
  const now = Date.now();
  if (cached && now - cached.ts < CLIENT_TTL) return cached.data;

  try {
    const res = await fetch(`/api/naver-shopping?q=${encodeURIComponent(q)}`);
    const data = await res.json();
    if (!data.ok) return { ok: false, error: data.error || 'fetch failed', items: [] };
    CACHE.set(q, { ts: now, data });
    return data;
  } catch (e) {
    return { ok: false, error: e.message, items: [] };
  }
}

export async function fetchManyNaverPrices(priceItems, concurrency = 3) {
  const result = new Map();
  const queue = [...priceItems].filter(p => p?.name);
  async function worker() {
    while (queue.length > 0) {
      const p = queue.shift();
      // 동의어 사전 적용: "백오이" → "오이"로 검색 (브로드 매칭)
      const searchName = normalizeFoodName(p.name);
      const r = await fetchNaverShopping(searchName || p.name);
      // 결과 키는 원본 이름으로 (단가표 매칭용)
      if (r.ok) result.set(p.name, r);
    }
  }
  const workers = Array(Math.min(concurrency, queue.length)).fill(0).map(() => worker());
  await Promise.all(workers);
  return result;
}

// 상품명에서 무게(g) 추출
export function parseWeightG(title) {
  if (!title) return 0;
  const t = String(title).toLowerCase().replace(/\s+/g, '');

  let g = 0;
  const kgMatch = t.match(/(\d+(?:\.\d+)?)kg/);
  if (kgMatch) g = parseFloat(kgMatch[1]) * 1000;
  else {
    const gMatch = t.match(/(\d+(?:\.\d+)?)g(?:[^a-z]|$)/);
    if (gMatch) g = parseFloat(gMatch[1]);
  }
  if (g === 0) {
    const lMatch = t.match(/(\d+(?:\.\d+)?)l(?:[^a-z]|$)/);
    if (lMatch) g = parseFloat(lMatch[1]) * 1000;
  }
  if (g === 0) {
    const mlMatch = t.match(/(\d+(?:\.\d+)?)ml/);
    if (mlMatch) g = parseFloat(mlMatch[1]);
  }
  if (g === 0) return 0;

  // 패키지 수량
  const xMatch =
    t.match(/[x×*](\d+)(?:개|팩|봉|개입|봉지|입|p)/) ||
    t.match(/(\d+)(?:개입|봉지)/) ||
    t.match(/[x×*](\d+)(?:[^0-9]|$)/);
  if (xMatch) {
    const cnt = parseInt(xMatch[1], 10);
    if (cnt >= 2 && cnt <= 100) g *= cnt;
  }
  return g;
}

export function naverItemPricePerKg(item) {
  const g = parseWeightG(item?.title);
  const price = Number(item?.lprice) || 0;
  if (g === 0 || price === 0) return 0;
  return Math.round(price / (g / 1000));
}

export function lowestPricePerKg(items) {
  if (!Array.isArray(items)) return { pricePerKg: 0, item: null };
  let bestKg = 0;
  let bestItem = null;
  for (const it of items) {
    const ppkg = naverItemPricePerKg(it);
    if (ppkg > 0 && (bestKg === 0 || ppkg < bestKg)) {
      bestKg = ppkg;
      bestItem = it;
    }
  }
  return { pricePerKg: bestKg, item: bestItem };
}

export function userPricePerKg(priceItem) {
  if (!priceItem?.price || !priceItem?.net_g) return 0;
  const kg = Number(priceItem.net_g) / 1000;
  if (kg <= 0) return 0;
  return Math.round(Number(priceItem.price) / kg);
}

export function compareUserVsNaver(priceItem, naverData) {
  const userKg = userPricePerKg(priceItem);
  const { pricePerKg: naverKg, item } = lowestPricePerKg(naverData?.items || []);
  if (userKg === 0 || naverKg === 0) {
    return { userKg, naverKg, item, diffPct: null, status: 'unknown' };
  }
  const diffPct = ((userKg - naverKg) / naverKg) * 100;
  let status = 'fair';
  if (diffPct < -10) status = 'cheap';
  else if (diffPct > 15) status = 'expensive';
  return {
    userKg, naverKg, item,
    diffPct: Math.round(diffPct * 10) / 10,
    status,
  };
}
